/**
 * CategoryManagement Component
 * Allows organizations to create, edit, and manage custom category templates
 * Shows all domains in collapsible sections
 */

import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, GripVertical, ChevronRight, ChevronDown, Type, Upload, Square, Circle, Palette, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useMergedCategoriesHierarchy,
  type MergedCategory,
  type DomainType,
} from '../hooks/useMergedCategories';
import {
  organizationCategoryService,
  type CreateCategoryDto,
} from '../services/organizationCategoryService';
import { CategoryIcon, validateSVG } from './CategoryIcon';

interface CategoryManagementProps {
  className?: string;
}

interface CategoryFormData {
  category: string;
  subcategory: string;
  display_name: string;
  abbreviation: string;
  fill_color: string;
  border_color: string;
  text_color: string;
  icon_shape: 'square' | 'circle';
  icon_type: 'abbreviation' | 'svg';
  icon_svg: string | null;
}

const DEFAULT_FORM_DATA: CategoryFormData = {
  category: '',
  subcategory: '',
  display_name: '',
  abbreviation: '',
  fill_color: '#6B7280',
  border_color: '#374151',
  text_color: '#FFFFFF',
  icon_shape: 'square',
  icon_type: 'abbreviation',
  icon_svg: null,
};

const COLOR_PRESETS = [
  { name: 'Gray', fill: '#6B7280', border: '#374151' },
  { name: 'Blue', fill: '#3B82F6', border: '#1D4ED8' },
  { name: 'Green', fill: '#22C55E', border: '#15803D' },
  { name: 'Yellow', fill: '#EAB308', border: '#A16207' },
  { name: 'Orange', fill: '#F97316', border: '#C2410C' },
  { name: 'Red', fill: '#EF4444', border: '#B91C1C' },
  { name: 'Purple', fill: '#A855F7', border: '#7C3AED' },
  { name: 'Pink', fill: '#EC4899', border: '#BE185D' },
  { name: 'Teal', fill: '#14B8A6', border: '#0D9488' },
];

const DOMAIN_OPTIONS: { value: DomainType; label: string }[] = [
  { value: 'lv_products', label: 'Products' },
  { value: 'lighting_fixtures', label: 'Lighting Fixtures' },
];

export function CategoryManagement({ className }: CategoryManagementProps) {
  const queryClient = useQueryClient();

  // Fetch categories for displayed domains
  const lvProductsData = useMergedCategoriesHierarchy('lv_products');
  const lightingFixturesData = useMergedCategoriesHierarchy('lighting_fixtures');

  const domainData: Partial<Record<DomainType, typeof lvProductsData>> = {
    lv_products: lvProductsData,
    lighting_fixtures: lightingFixturesData,
  };

  // Dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MergedCategory | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<DomainType>('lv_products');
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);

  // Track expanded states
  const [expandedDomains, setExpandedDomains] = useState<Set<DomainType>>(new Set(['lv_products']));
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // Helper to invalidate all related queries
  const invalidateCategoryQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
    queryClient.invalidateQueries({ queryKey: ['category-templates'] });
  };

  // Toggle domain expansion
  const toggleDomainExpansion = (domain: DomainType) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  // Toggle parent category expansion
  const toggleParentExpansion = (categoryKey: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryDto) => organizationCategoryService.createCategory(data),
    onSuccess: () => {
      invalidateCategoryQueries();
      toast.success('Category created successfully');
      setIsCreateDialogOpen(false);
      setFormData(DEFAULT_FORM_DATA);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create category: ${error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCategoryDto> }) =>
      organizationCategoryService.updateCategory(id, data),
    onSuccess: () => {
      invalidateCategoryQueries();
      toast.success('Category updated successfully');
      setIsEditDialogOpen(false);
      setSelectedCategory(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update category: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => organizationCategoryService.deleteCategory(id),
    onSuccess: () => {
      invalidateCategoryQueries();
      toast.success('Category deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete category: ${error.message}`);
    },
  });

  // Handle opening create dialog for a specific domain
  const handleOpenCreate = (domain: DomainType) => {
    setSelectedDomain(domain);
    setFormData(DEFAULT_FORM_DATA);
    setIsCreateDialogOpen(true);
  };

  // Handle create
  const handleCreate = () => {
    if (!formData.category.trim()) {
      toast.error('Category name is required');
      return;
    }

    createMutation.mutate({
      domain: selectedDomain,
      category: formData.category.trim(),
      subcategory: formData.subcategory.trim() || undefined,
      display_name: formData.display_name.trim() || formData.category.trim(),
      abbreviation: formData.abbreviation.trim() || undefined,
      fill_color: formData.fill_color,
      border_color: formData.border_color,
      text_color: formData.text_color,
      icon_shape: formData.icon_shape,
      icon_type: formData.icon_type,
      icon_svg: formData.icon_type === 'svg' ? formData.icon_svg : undefined,
    });
  };

  // Handle edit
  const handleEdit = (category: MergedCategory, domain: DomainType) => {
    if (category.is_system) {
      toast.error('System categories cannot be edited');
      return;
    }
    setSelectedCategory(category);
    setSelectedDomain(domain);
    setFormData({
      category: category.category,
      subcategory: category.subcategory || '',
      display_name: category.display_name,
      abbreviation: category.abbreviation || '',
      fill_color: category.fill_color,
      border_color: category.border_color || '#374151',
      text_color: category.text_color || '#FFFFFF',
      icon_shape:
        (category.icon_shape === 'rectangle' ? 'square' : category.icon_shape) || 'square',
      icon_type: category.icon_type || 'abbreviation',
      icon_svg: category.icon_svg || null,
    });
    setIsEditDialogOpen(true);
  };

  // Handle update
  const handleUpdate = () => {
    if (!selectedCategory?.org_category_id) return;

    updateMutation.mutate({
      id: selectedCategory.org_category_id,
      data: {
        display_name: formData.display_name.trim() || formData.category.trim(),
        abbreviation: formData.abbreviation.trim() || undefined,
        fill_color: formData.fill_color,
        border_color: formData.border_color,
        text_color: formData.text_color,
        icon_shape: formData.icon_shape,
        icon_type: formData.icon_type,
        icon_svg: formData.icon_type === 'svg' ? formData.icon_svg : undefined,
      },
    });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (category: MergedCategory) => {
    if (category.is_system) {
      toast.error('System categories cannot be deleted');
      return;
    }
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = () => {
    if (!selectedCategory?.org_category_id) return;
    deleteMutation.mutate(selectedCategory.org_category_id);
  };

  // Helper to render a category row
  const renderCategoryRow = (
    category: MergedCategory,
    domain: DomainType,
    isSubcategory = false,
    parentKey?: string,
  ) => {
    const isCustom = !category.is_system;
    const hierarchy = domainData[domain].hierarchy;
    const subcategories = !isSubcategory ? hierarchy.children.get(category.category) || [] : [];
    const hasSubcategories = subcategories.length > 0;
    const categoryKey = parentKey
      ? `${domain}-${parentKey}-${category.category}`
      : `${domain}-${category.category}`;
    const isExpanded = expandedParents.has(categoryKey);

    return (
      <div key={category.id}>
        <div
          className={cn(
            'flex items-center justify-between p-3 hover:bg-muted/50',
            isSubcategory && 'pl-10 bg-muted/20',
            !isCustom && 'opacity-75',
          )}
        >
          <div className="flex items-center gap-3">
            {/* Expand/Collapse or Grip */}
            {!isSubcategory && hasSubcategories ? (
              <button
                onClick={() => toggleParentExpansion(categoryKey)}
                className="p-1 hover:bg-muted rounded"
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform',
                    isExpanded && 'rotate-90',
                  )}
                />
              </button>
            ) : isCustom ? (
              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
            ) : (
              <div className="w-6" />
            )}
            <CategoryIcon
              category={{
                icon_type: category.icon_type,
                abbreviation: category.abbreviation,
                icon_shape: category.icon_shape,
                fill_color: category.fill_color,
                border_color: category.border_color,
                text_color: category.text_color,
                icon_svg: category.icon_svg,
              }}
              size={24}
            />
            <div>
              <div className="font-medium">{category.display_name}</div>
              {hasSubcategories && !isSubcategory && (
                <div className="text-xs text-muted-foreground">
                  {subcategories.length} subcategor{subcategories.length === 1 ? 'y' : 'ies'}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCustom ? (
              <>
                <Button variant="ghost" size="icon" onClick={() => handleEdit(category, domain)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDeleteConfirm(category)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="text-xs">
                System
              </Badge>
            )}
          </div>
        </div>
        {/* Render subcategories if expanded */}
        {!isSubcategory &&
          isExpanded &&
          subcategories.map(sub => renderCategoryRow(sub, domain, true, category.category))}
      </div>
    );
  };

  // Render a domain section
  const renderDomainSection = (domain: DomainType, label: string) => {
    const { hierarchy, isLoading } = domainData[domain];
    const isExpanded = expandedDomains.has(domain);

    const systemParents = hierarchy.parents.filter(c => c.is_system);
    const customParents = hierarchy.parents.filter(c => !c.is_system);
    const totalCategories = hierarchy.parents.length;

    return (
      <Collapsible
        key={domain}
        open={isExpanded}
        onOpenChange={() => toggleDomainExpansion(domain)}
        className="border rounded-lg"
      >
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <h3 className="font-semibold">{label}</h3>
                <p className="text-sm text-muted-foreground">
                  {totalCategories} categor{totalCategories === 1 ? 'y' : 'ies'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={e => {
                e.stopPropagation();
                handleOpenCreate(domain);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Category
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t">
            {isLoading ? (
              <div className="p-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-10 bg-muted rounded" />
                  <div className="h-10 bg-muted rounded" />
                </div>
              </div>
            ) : hierarchy.parents.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No categories yet. Click "Add Category" to create one.
              </div>
            ) : (
              <div className="divide-y">
                {/* Custom Categories */}
                {customParents.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-muted/30">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Custom Categories
                      </span>
                    </div>
                    <div className="divide-y">
                      {customParents.map(category => renderCategoryRow(category, domain))}
                    </div>
                  </div>
                )}

                {/* System Categories */}
                {systemParents.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-muted/30 flex items-center gap-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        System Categories
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        Read-only
                      </Badge>
                    </div>
                    <div className="divide-y">
                      {systemParents.map(category => renderCategoryRow(category, domain))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  // Get current domain's parent categories for the form
  const currentParentCategories = domainData[selectedDomain]?.hierarchy.parents || [];

  return (
    <div className={cn('space-y-4', className)}>
      {/* Domain Sections */}
      {DOMAIN_OPTIONS.map(opt => renderDomainSection(opt.value, opt.label))}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Category</DialogTitle>
            <DialogDescription>
              Add a new custom category for{' '}
              {DOMAIN_OPTIONS.find(d => d.value === selectedDomain)?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            <CategoryForm
              formData={formData}
              setFormData={setFormData}
              parentCategories={currentParentCategories}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>Update category settings</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            <CategoryForm formData={formData} setFormData={setFormData} isEdit />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCategory?.display_name}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Form component for create/edit - Modern, minimalist design
function CategoryForm({
  formData,
  setFormData,
  isEdit = false,
  parentCategories = [],
}: {
  formData: CategoryFormData;
  setFormData: React.Dispatch<React.SetStateAction<CategoryFormData>>;
  isEdit?: boolean;
  parentCategories?: MergedCategory[];
}) {
  const [isCreatingSubcategory, setIsCreatingSubcategory] = useState(!!formData.subcategory);
  const [svgValidationWarnings, setSvgValidationWarnings] = useState<string[]>([]);
  const [showCustomColors, setShowCustomColors] = useState(false);

  // Handle SVG file upload
  const handleSVGUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const svgContent = await file.text();
      const validation = validateSVG(svgContent);

      if (!validation.valid) {
        toast.error(validation.error || 'Invalid SVG file');
        e.target.value = '';
        return;
      }

      setSvgValidationWarnings(validation.warnings);
      const svgToStore = validation.resizedSvg || svgContent;

      setFormData(prev => ({
        ...prev,
        icon_type: 'svg',
        icon_svg: svgToStore,
      }));
    } catch (error) {
      toast.error('Failed to read SVG file');
      e.target.value = '';
    }
  };

  // Preview the icon
  const previewConfig = {
    icon_type: formData.icon_type,
    abbreviation:
      formData.abbreviation ||
      formData.category
        .split(/[\s_-]+/)
        .slice(0, 3)
        .map(w => w[0]?.toUpperCase() || '')
        .join(''),
    icon_shape: formData.icon_shape,
    fill_color: formData.fill_color,
    border_color: formData.border_color,
    text_color: formData.text_color,
    icon_svg: formData.icon_svg,
  };

  return (
    <div className="space-y-4 py-2">
      {/* Category Type Toggle - Segmented Control */}
      {!isEdit && parentCategories.length > 0 && (
        <div className="flex rounded-lg border bg-muted/30 p-1 gap-1">
          <button
            type="button"
            onClick={() => {
              setIsCreatingSubcategory(false);
              setFormData(prev => ({ ...prev, subcategory: '' }));
            }}
            className={cn(
              'flex-1 px-3 py-1.5 rounded-md text-sm transition-all',
              !isCreatingSubcategory
                ? 'bg-background shadow-sm font-medium'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Parent Category
          </button>
          <button
            type="button"
            onClick={() => setIsCreatingSubcategory(true)}
            className={cn(
              'flex-1 px-3 py-1.5 rounded-md text-sm transition-all',
              isCreatingSubcategory
                ? 'bg-background shadow-sm font-medium'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Subcategory
          </button>
        </div>
      )}

      {/* Parent Category Selection (for subcategories) */}
      {!isEdit && isCreatingSubcategory && parentCategories.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Parent Category</Label>
          <Select
            value={formData.category}
            onValueChange={value => setFormData(prev => ({ ...prev, category: value }))}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select parent category" />
            </SelectTrigger>
            <SelectContent>
              {parentCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.category}>
                  {cat.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Category Name (for parent categories) */}
      {(!isCreatingSubcategory || isEdit) && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Category Name</Label>
          <Input
            className="h-9"
            value={formData.category}
            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
            placeholder="e.g., Audio Equipment"
            disabled={isEdit}
          />
        </div>
      )}

      {/* Subcategory Name (when creating subcategory) */}
      {isCreatingSubcategory && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Subcategory Name</Label>
          <Input
            className="h-9"
            value={formData.subcategory}
            onChange={e => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
            placeholder="e.g., In-Ceiling Speakers"
            disabled={isEdit}
          />
        </div>
      )}

      {/* Display Name */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Display Name</Label>
        <Input
          className="h-9"
          value={formData.display_name}
          onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
          placeholder={formData.category || 'Display name'}
        />
      </div>

      <Separator className="my-4" />

      {/* Icon Type Toggle - Segmented Control */}
      <div className="flex rounded-lg border bg-muted/30 p-1 gap-1">
        <button
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, icon_type: 'abbreviation' }))}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all',
            formData.icon_type === 'abbreviation'
              ? 'bg-background shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Type className="h-3.5 w-3.5" />
          Text
        </button>
        <button
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, icon_type: 'svg' }))}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all',
            formData.icon_type === 'svg'
              ? 'bg-background shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Upload className="h-3.5 w-3.5" />
          SVG
        </button>
      </div>

      {/* Preview - Centered */}
      <div className="flex justify-center py-3">
        <div className="p-4 rounded-xl bg-muted/40 border border-border/50">
          <CategoryIcon category={previewConfig} size={56} />
        </div>
      </div>

      {/* SVG Upload Section */}
      {formData.icon_type === 'svg' && (
        <div className="space-y-3">
          {formData.icon_svg ? (
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/50">
              <span className="text-sm text-muted-foreground">SVG uploaded</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-destructive hover:text-destructive"
                onClick={() => {
                  setFormData(prev => ({ ...prev, icon_svg: null, icon_type: 'abbreviation' }));
                  setSvgValidationWarnings([]);
                }}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed border-border/50 hover:border-primary/50 cursor-pointer transition-colors">
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm font-medium">Upload SVG</span>
              <span className="text-xs text-muted-foreground mt-1">Click or drag file</span>
              <input
                type="file"
                accept=".svg"
                onChange={handleSVGUpload}
                className="hidden"
              />
            </label>
          )}
          {svgValidationWarnings.length > 0 && (
            <div className="text-amber-600 text-xs p-2 bg-amber-50 rounded-md">
              {svgValidationWarnings.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text Icon Options */}
      {formData.icon_type === 'abbreviation' && (
        <div className="space-y-4">
          {/* Abbreviation Input */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Abbreviation</Label>
            <Input
              value={formData.abbreviation}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  abbreviation: e.target.value.toUpperCase().slice(0, 4),
                }))
              }
              placeholder="Auto-generated"
              maxLength={4}
              className="h-9 text-center font-mono tracking-wider"
            />
          </div>

          {/* Shape Toggle */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground min-w-[50px]">Shape</Label>
            <div className="flex rounded-md border bg-muted/30 p-0.5">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, icon_shape: 'square' }))}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-all',
                  formData.icon_shape === 'square'
                    ? 'bg-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Square className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, icon_shape: 'circle' }))}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-all',
                  formData.icon_shape === 'circle'
                    ? 'bg-background shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Circle className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Color Presets */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Color</Label>
            <div className="flex flex-wrap gap-1.5">
              {COLOR_PRESETS.map(preset => (
                <button
                  key={preset.name}
                  type="button"
                  className={cn(
                    'w-7 h-7 rounded-md border transition-all',
                    formData.fill_color === preset.fill
                      ? 'ring-2 ring-primary ring-offset-1'
                      : 'hover:scale-105',
                  )}
                  style={{ backgroundColor: preset.fill, borderColor: preset.border }}
                  onClick={() =>
                    setFormData(prev => ({
                      ...prev,
                      fill_color: preset.fill,
                      border_color: preset.border,
                    }))
                  }
                  title={preset.name}
                />
              ))}
            </div>
          </div>

          {/* Custom Colors - Collapsible */}
          <Collapsible open={showCustomColors} onOpenChange={setShowCustomColors}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Palette className="h-3.5 w-3.5" />
                Custom colors
                <ChevronDown className={cn(
                  'h-3 w-3 transition-transform',
                  showCustomColors && 'rotate-180'
                )} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Fill</Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      value={formData.fill_color}
                      onChange={e => setFormData(prev => ({ ...prev, fill_color: e.target.value }))}
                      className="w-8 h-8 p-0.5 cursor-pointer"
                    />
                    <Input
                      value={formData.fill_color}
                      onChange={e => setFormData(prev => ({ ...prev, fill_color: e.target.value }))}
                      className="flex-1 h-8 text-xs font-mono"
                    />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Border</Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      value={formData.border_color}
                      onChange={e => setFormData(prev => ({ ...prev, border_color: e.target.value }))}
                      className="w-8 h-8 p-0.5 cursor-pointer"
                    />
                    <Input
                      value={formData.border_color}
                      onChange={e => setFormData(prev => ({ ...prev, border_color: e.target.value }))}
                      className="flex-1 h-8 text-xs font-mono"
                    />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Text</Label>
                  <div className="flex gap-1">
                    <Input
                      type="color"
                      value={formData.text_color}
                      onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                      className="w-8 h-8 p-0.5 cursor-pointer"
                    />
                    <Input
                      value={formData.text_color}
                      onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                      className="flex-1 h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}
